import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { supabase } from '../../lib/supabase';
import { QUESTIONARIOS, TIPOS } from '../../lib/questionarios';
import AdminLayout from '../../components/AdminLayout';
import ExameResultados from '../../components/ExameResultados';

const card = {
  background: 'var(--surface)',
  border: '1px solid var(--divider)',
  borderRadius: 'var(--r-card)',
  padding: '22px',
  boxShadow: 'var(--sh-card)',
};

function fmtData(valor) {
  if (!valor) return '—';
  return new Date(`${String(valor).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR');
}

function valorTexto(valor) {
  if (valor === null || valor === undefined || valor === '') return '—';
  return String(valor);
}

function Linha({ label, value }) {
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid var(--divider)' }}>
      <div style={{ color: 'var(--text-3)', fontSize: '10px', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
      <div style={{ color: value ? 'var(--text)' : 'var(--text-2)', fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{valorTexto(value)}</div>
    </div>
  );
}

export default function CadastrarPerfil() {
  const { alunas, atualizarAluna } = useAuth();
  const { perfis, exames } = useData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [alunaId, setAlunaId] = useState(searchParams.get('aluna') || '');
  const [dados, setDados] = useState({ questionarios: [], avaliacoes: [], evolucao: [] });
  const [loading, setLoading] = useState(false);
  const [editandoIdentificacao, setEditandoIdentificacao] = useState(false);
  const [identificacao, setIdentificacao] = useState({});
  const [salvandoIdentificacao, setSalvandoIdentificacao] = useState(false);
  const [gerandoResumo, setGerandoResumo] = useState(false);
  const [salvandoResumo, setSalvandoResumo] = useState(false);
  const [resumoMsg, setResumoMsg] = useState('');
  const [resumos, setResumos] = useState({ equipe: '', alunoRascunho: '', alunoAprovado: '', geradoEm: '', aprovadoEm: '' });

  useEffect(() => {
    if (!alunaId && alunas.length) {
      const primeiroId = alunas[0].id;
      setAlunaId(primeiroId);
      setSearchParams({ aluna: primeiroId }, { replace: true });
    }
  }, [alunaId, alunas, setSearchParams]);

  useEffect(() => {
    if (!alunaId) return;
    setLoading(true);
    Promise.all([
      supabase.from('questionario_respostas').select('*').eq('aluna_id', alunaId).order('created_at', { ascending: false }),
      supabase.from('avaliacoes_treino').select('*').eq('aluna_id', alunaId).order('data', { ascending: false }),
      supabase.from('evolucao_fisica').select('*').eq('aluna_id', alunaId).order('data', { ascending: false }),
    ]).then(([questionarios, avaliacao, evolucao]) => {
      setDados({
        questionarios: questionarios.data || [],
        avaliacoes: avaliacao.data || [],
        evolucao: evolucao.data || [],
      });
      setLoading(false);
    });
  }, [alunaId]);

  const aluna = alunas.find(item => item.id === alunaId);
  const perfil = perfis[alunaId] || {};
  const examesDaAluna = exames.filter(exame => exame.alunaId === alunaId);
  const ultimosPorTipo = useMemo(() => {
    const mapa = {};
    dados.questionarios.forEach(registro => {
      if (!mapa[registro.tipo]) mapa[registro.tipo] = registro;
    });
    return mapa;
  }, [dados.questionarios]);
  const anamnese = ultimosPorTipo.anamnese;
  const respostasAnamnese = Array.isArray(anamnese?.respostas) ? anamnese.respostas : [];
  const perguntasAnamnese = QUESTIONARIOS.anamnese.perguntas;
  const anamneseTreino = ultimosPorTipo.anamnese_treino;
  const respostasAnamneseTreino = Array.isArray(anamneseTreino?.respostas) ? anamneseTreino.respostas : [];
  const perguntasAnamneseTreino = QUESTIONARIOS.anamnese_treino.perguntas;
  const evolucao = dados.evolucao[0] || null;
  const avaliacao = dados.avaliacoes[0] || null;

  useEffect(() => {
    if (!aluna) return;
    setIdentificacao({
      name: aluna.name || '',
      email: aluna.email || '',
      telefone: perfil.telefone || aluna.telefone || '',
      dataNascimento: perfil.dataNascimento || '',
      cidade: perfil.cidade || '',
      pais: perfil.pais || '',
      altura: perfil.altura || '',
    });
    const salvo = perfil.anamnese?._resumoProntuario || {};
    setResumos({
      equipe: salvo.equipe || '',
      alunoRascunho: salvo.alunoRascunho || '',
      alunoAprovado: salvo.alunoAprovado || '',
      geradoEm: salvo.geradoEm || '',
      aprovadoEm: salvo.aprovadoEm || '',
    });
  }, [aluna, perfil]);

  function trocarAluna(id) {
    setAlunaId(id);
    setSearchParams({ aluna: id }, { replace: true });
  }

  async function salvarIdentificacao() {
    setSalvandoIdentificacao(true);
    await atualizarAluna(alunaId, {
      name: identificacao.name,
      email: identificacao.email,
      telefone: identificacao.telefone,
    });
    await supabase.from('perfis').upsert({
      aluna_id: alunaId,
      telefone: identificacao.telefone || null,
      cidade: identificacao.cidade || null,
      pais: identificacao.pais || null,
      data_nascimento: identificacao.dataNascimento || null,
      altura: identificacao.altura || null,
      modalidade: perfil.modalidade || 'nutricao-treino',
      anamnese: perfil.anamnese || {},
    });
    setSalvandoIdentificacao(false);
    setEditandoIdentificacao(false);
  }

  async function gerarResumo() {
    setGerandoResumo(true);
    setResumoMsg('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/.netlify/functions/resumir-prontuario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          prontuario: {
            alunaId,
            identificacao,
            questionarios: dados.questionarios,
            avaliacoesEquipe: dados.avaliacoes,
            evolucaoFisica: dados.evolucao,
            exames: examesDaAluna,
          },
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Não foi possível gerar o resumo.');
      setResumos(atual => ({
        ...atual,
        equipe: body.equipe || '',
        alunoRascunho: body.aluno || '',
        geradoEm: new Date().toISOString(),
      }));
      setResumoMsg('Resumo gerado. Revise o texto do aluno antes de aprovar.');
    } catch (error) {
      setResumoMsg(error.message);
    }
    setGerandoResumo(false);
  }

  async function persistirResumos(proximos) {
    setSalvandoResumo(true);
    const anamneseAtual = perfil.anamnese || {};
    const { error } = await supabase.from('perfis').upsert({
      aluna_id: alunaId,
      telefone: perfil.telefone || aluna.telefone || null,
      cidade: perfil.cidade || null,
      pais: perfil.pais || null,
      data_nascimento: perfil.dataNascimento || null,
      altura: perfil.altura || null,
      modalidade: perfil.modalidade || 'nutricao-treino',
      anamnese: { ...anamneseAtual, _resumoProntuario: proximos },
    });
    setResumoMsg(error ? `Não foi possível salvar: ${error.message}` : 'Resumo salvo.');
    setSalvandoResumo(false);
  }

  async function salvarRascunhos() {
    await persistirResumos(resumos);
  }

  async function aprovarParaAluno() {
    const proximos = { ...resumos, alunoAprovado: resumos.alunoRascunho, aprovadoEm: new Date().toISOString() };
    setResumos(proximos);
    await persistirResumos(proximos);
    setResumoMsg('Resumo aprovado e disponibilizado para o aluno.');
  }

  return (
    <AdminLayout>
      <style>{`
        .prontuario-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; }
        .prontuario-span { grid-column:1 / -1; }
        @media(max-width:850px){ .prontuario-grid{grid-template-columns:1fr}.prontuario-span{grid-column:auto} }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
        <div>
          <div style={{ color: 'var(--accent)', fontSize: '11px', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: '6px' }}>Visão consolidada</div>
          <h1 style={{ margin: 0, color: 'var(--text)', fontSize: '34px' }}>Prontuário <em style={{ color: 'var(--accent)' }}>do aluno</em></h1>
          <p style={{ margin: '7px 0 0', color: 'var(--text-2)', fontSize: '13px' }}>Informações vindas dos questionários e das avaliações registradas pela equipe.</p>
        </div>
        <button type="button" onClick={() => navigate('/admin/alunas')} style={{ padding: '10px 18px', border: '1px solid var(--divider)', borderRadius: 'var(--r-btn)', background: 'transparent', color: 'var(--text-2)', cursor: 'pointer' }}>← Voltar</button>
      </div>

      <div style={{ ...card, marginBottom: '16px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'end', gap: '14px' }}>
        <label style={{ display: 'block' }}>
          <span style={{ display: 'block', color: 'var(--text-3)', fontSize: '10px', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '7px' }}>Aluno</span>
          <select value={alunaId} onChange={event => trocarAluna(event.target.value)} style={{ width: '100%', padding: '12px 14px', border: '1.5px solid var(--divider)', borderRadius: 'var(--r-input)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: '14px' }}>
            {alunas.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <button type="button" onClick={() => navigate(`/admin/questionarios?aluna=${alunaId}`)} style={{ padding: '12px 18px', border: 0, borderRadius: 'var(--r-btn)', background: 'var(--text)', color: 'var(--bg)', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Ver respostas completas</button>
      </div>

      {!aluna || loading ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--text-2)' }}>{loading ? 'Carregando prontuário...' : 'Nenhum aluno cadastrado.'}</div>
      ) : (
        <div className="prontuario-grid">
          <section style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Identificação</div>
              <button type="button" onClick={() => setEditandoIdentificacao(valor => !valor)} style={{ border: '1px solid var(--divider)', borderRadius: '999px', background: 'transparent', color: 'var(--accent)', padding: '6px 11px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>{editandoIdentificacao ? 'Cancelar' : '✎ Editar dados'}</button>
            </div>
            {editandoIdentificacao ? (
              <div style={{ display: 'grid', gap: '10px' }}>
                {[
                  ['name', 'Nome', 'text'], ['email', 'E-mail', 'email'], ['telefone', 'WhatsApp', 'text'],
                  ['dataNascimento', 'Data de nascimento', 'date'], ['cidade', 'Cidade', 'text'],
                  ['pais', 'País', 'text'], ['altura', 'Altura', 'text'],
                ].map(([key, label, type]) => (
                  <label key={key} style={{ color: 'var(--text-3)', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>
                    {label}
                    <input type={type} value={identificacao[key] || ''} onChange={event => setIdentificacao(atual => ({ ...atual, [key]: event.target.value }))} style={{ width: '100%', boxSizing: 'border-box', marginTop: '5px', padding: '10px 12px', border: '1px solid var(--divider)', borderRadius: '14px', background: 'var(--surface-2)', color: 'var(--text)', fontSize: '13px' }} />
                  </label>
                ))}
                <button type="button" disabled={salvandoIdentificacao} onClick={salvarIdentificacao} style={{ border: 0, borderRadius: '999px', background: 'var(--text)', color: 'var(--bg)', padding: '11px', fontWeight: 700, cursor: 'pointer' }}>{salvandoIdentificacao ? 'Salvando...' : 'Salvar identificação'}</button>
              </div>
            ) : (
              <>
                <Linha label="Nome" value={identificacao.name} />
                <Linha label="E-mail" value={identificacao.email} />
                <Linha label="WhatsApp" value={identificacao.telefone} />
                <Linha label="Data de nascimento" value={identificacao.dataNascimento ? fmtData(identificacao.dataNascimento) : ''} />
                <Linha label="Cidade / país" value={[identificacao.cidade, identificacao.pais].filter(Boolean).join(' · ')} />
                <Linha label="Altura" value={identificacao.altura} />
              </>
            )}
          </section>

          <section style={card}>
            <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '14px' }}>Questionários iniciais</div>
            {TIPOS.map(tipo => {
              const registro = ultimosPorTipo[tipo];
              return (
                <div key={tipo} style={{ display: 'grid', gridTemplateColumns: '32px 1fr auto', alignItems: 'center', gap: '10px', padding: '11px 0', borderBottom: '1px solid var(--divider)' }}>
                  <span style={{ fontSize: '18px' }}>{QUESTIONARIOS[tipo].icone}</span>
                  <div>
                    <strong style={{ display: 'block', color: 'var(--text)', fontSize: '12px' }}>{QUESTIONARIOS[tipo].titulo}</strong>
                    <small style={{ color: 'var(--text-2)' }}>{registro ? `Respondido em ${fmtData(registro.created_at)}` : 'Ainda não respondido'}</small>
                  </div>
                  {registro ? (
                    <button type="button" onClick={() => navigate(`/admin/questionarios?aluna=${alunaId}&tipo=${tipo}`)} style={{ border: '1px solid var(--divider)', borderRadius: '999px', background: 'transparent', color: 'var(--accent)', padding: '6px 10px', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>Visualizar →</button>
                  ) : (
                    <span style={{ padding: '4px 9px', borderRadius: '999px', background: 'rgba(137,91,64,.10)', color: 'var(--accent)', fontSize: '10px', fontWeight: 700 }}>Pendente</span>
                  )}
                </div>
              );
            })}
          </section>

          <section className="prontuario-span" style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Anamnese inicial enviada pelo aluno</div>
              {anamnese && <small style={{ color: 'var(--text-2)' }}>Fonte: questionário de {fmtData(anamnese.created_at)}</small>}
            </div>
            {!anamnese ? (
              <div style={{ color: 'var(--text-2)', fontSize: '13px', padding: '16px 0' }}>O aluno ainda não respondeu à Anamnese Inicial.</div>
            ) : perguntasAnamnese.map((pergunta, index) => (
              <Linha key={pergunta.label} label={pergunta.label} value={respostasAnamnese[index]} />
            ))}
          </section>

          <section className="prontuario-span" style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Anamnese de treino</div>
              {anamneseTreino && <small style={{ color: 'var(--text-2)' }}>Fonte: questionário de {fmtData(anamneseTreino.created_at)}</small>}
            </div>
            {!anamneseTreino ? (
              <div style={{ color: 'var(--text-2)', fontSize: '13px', padding: '16px 0' }}>O aluno ainda não respondeu à Anamnese de Treino.</div>
            ) : perguntasAnamneseTreino.map((pergunta, index) => (
              <Linha key={pergunta.label} label={pergunta.label} value={respostasAnamneseTreino[index]} />
            ))}
          </section>

          <section style={card}>
            <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '8px' }}>Avaliação da Carol</div>
            {!avaliacao ? (
              <div style={{ color: 'var(--text-2)', fontSize: '13px', padding: '12px 0' }}>Nenhuma avaliação de treino registrada.</div>
            ) : (
              <>
                <Linha label="Data da avaliação" value={fmtData(avaliacao.data)} />
                <Linha label="Objetivo" value={avaliacao.objetivo} />
                <Linha label="Restrições e saúde" value={avaliacao.restricoes} />
                <Linha label="Observações da consulta" value={avaliacao.observacoes} />
              </>
            )}
          </section>

          <section style={card}>
            <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '8px' }}>Última avaliação física</div>
            {!evolucao ? (
              <div style={{ color: 'var(--text-2)', fontSize: '13px', padding: '12px 0' }}>Nenhuma medida ou avaliação física registrada.</div>
            ) : (
              <>
                <Linha label="Data" value={fmtData(evolucao.data)} />
                <Linha label="Peso" value={evolucao.peso != null ? `${evolucao.peso} kg` : ''} />
                <Linha label="Gordura corporal" value={evolucao.gordura != null ? `${evolucao.gordura}%` : ''} />
                <Linha label="Cintura / abdômen / quadril" value={[evolucao.cintura, evolucao.abdomen, evolucao.quadril].map(valor => valor != null ? `${valor} cm` : '—').join(' · ')} />
                <Linha label="Observações" value={evolucao.observacoes} />
              </>
            )}
          </section>

          {examesDaAluna.some(exame => exame.resultados?.length > 0) && (
            <section className="prontuario-span" style={card}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '14px' }}>Exames e resultados</div>
              {examesDaAluna.filter(exame => exame.resultados?.length > 0).map(exame => (
                <div key={exame.id} style={{ marginBottom: '14px' }}>
                  <strong style={{ color: 'var(--text)', fontSize: '13px' }}>{exame.titulo}</strong>
                  <ExameResultados exame={exame} alunaId={alunaId} />
                </div>
              ))}
            </section>
          )}

          <section className="prontuario-span" style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '14px' }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Resumo do prontuário com IA</div>
                <small style={{ color: 'var(--text-2)' }}>Baseado nos registros existentes. Não substitui avaliação profissional.</small>
              </div>
              <button type="button" disabled={gerandoResumo} onClick={gerarResumo} style={{ border: 0, borderRadius: '999px', background: 'var(--accent)', color: 'var(--btn-text)', padding: '10px 15px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>{gerandoResumo ? 'Gerando...' : resumos.geradoEm ? '↻ Atualizar resumo com IA' : '✦ Gerar resumo com IA'}</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '14px' }}>
              <label style={{ color: 'var(--text)', fontSize: '12px', fontWeight: 700 }}>
                Resumo interno da equipe
                <textarea value={resumos.equipe} onChange={event => setResumos(atual => ({ ...atual, equipe: event.target.value }))} rows={8} placeholder="Gere o resumo ou escreva as observações consolidadas da equipe." style={{ width: '100%', boxSizing: 'border-box', marginTop: '7px', padding: '12px', border: '1px solid var(--divider)', borderRadius: '16px', background: 'var(--surface-2)', color: 'var(--text)', font: '13px/1.55 Inter, sans-serif', resize: 'vertical' }} />
              </label>
              <label style={{ color: 'var(--text)', fontSize: '12px', fontWeight: 700 }}>
                Resumo para o aluno — revisar antes de aprovar
                <textarea value={resumos.alunoRascunho} onChange={event => setResumos(atual => ({ ...atual, alunoRascunho: event.target.value }))} rows={8} placeholder="Este texto só aparece para o aluno depois da aprovação." style={{ width: '100%', boxSizing: 'border-box', marginTop: '7px', padding: '12px', border: '1px solid var(--divider)', borderRadius: '16px', background: 'var(--surface-2)', color: 'var(--text)', font: '13px/1.55 Inter, sans-serif', resize: 'vertical' }} />
              </label>
            </div>
            {resumos.alunoAprovado && <div style={{ marginTop: '10px', color: '#5e6840', fontSize: '11px', fontWeight: 700 }}>✓ Existe um resumo aprovado disponível para o aluno{resumos.aprovadoEm ? ` desde ${fmtData(resumos.aprovadoEm)}` : ''}.</div>}
            {resumoMsg && <div style={{ marginTop: '10px', color: 'var(--accent)', fontSize: '12px' }}>{resumoMsg}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
              <button type="button" disabled={salvandoResumo || (!resumos.equipe && !resumos.alunoRascunho)} onClick={salvarRascunhos} style={{ border: '1px solid var(--divider)', borderRadius: '999px', background: 'transparent', color: 'var(--text)', padding: '10px 14px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>Salvar rascunhos</button>
              <button type="button" disabled={salvandoResumo || !resumos.alunoRascunho} onClick={aprovarParaAluno} style={{ border: 0, borderRadius: '999px', background: '#5e6840', color: '#fff', padding: '10px 14px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>{salvandoResumo ? 'Salvando...' : 'Aprovar e disponibilizar ao aluno'}</button>
            </div>
          </section>
        </div>
      )}
    </AdminLayout>
  );
}
